import styled from 'styled-components';

import { Card } from '@components/card';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { Color, theme } from '@styles/theme';

import { useStore } from 'models/Provider';
import type { JobStatistics } from 'types/jobs';

import { SourcedDoughnut } from './sourced-doughnut';

export function OverviewStats({ data }: { data: JobStatistics }) {
  return (
    <Container>
      <Summary counts={data.counts} />
      <Status counts={data.counts} />
      <Source sources={data.sources} />
    </Container>
  );
}

function Summary({ counts }: { counts: JobStatistics['counts'] }) {
  const { t } = useStore();
  return (
    <StatsCard>
      <Stack space={4}>
        <Text font="bold14" color="purple80" caps text={t('job.statistics.overview.summary')} />
        <Stack space={2}>
          <SummaryValue count={counts.contentCleared} label="cleared" />
          <SummaryValue count={counts.totalViews} label="views" />
          <SummaryValue count={counts.applicants} label="applicants" />
        </Stack>
      </Stack>
    </StatsCard>
  );
}

function SummaryValue(props: { count: number; label: string }) {
  const { count, label } = props;
  const { t } = useStore();
  return (
    <Stack orientation="horizontal" space={2} justify="space-between">
      <Text ellipsis font="bold14" color="purple100" text={t('job.statistics.overview.' + label)} />
      <Text font="bold14" color="purple80" text={count} />
    </Stack>
  );
}

function Status(props: { counts: JobStatistics['counts'] }) {
  const { t } = useStore();
  const { counts } = props;
  const active = counts.applicants - counts.rejected - counts.hired - counts.retracted;

  return (
    <StatsCard>
      <Stack space={4}>
        <Text font="bold14" color="purple80" caps text={t('job.statistics.overview.status')} />
        <Stack space={2}>
          <StatusValue
            color="green100"
            fill={counts.applicants > 0 ? `${(counts.hired / counts.applicants) * 100}%` : '0%'}
            count={counts.hired}
            label="hired"
          />
          <StatusValue
            color="blue100"
            fill={counts.applicants > 0 ? `${(active / counts.applicants) * 100}%` : '0%'}
            count={active}
            label="active"
          />
          <StatusValue
            color="orange100"
            fill={counts.applicants > 0 ? `${(counts.retracted / counts.applicants) * 100}%` : '0%'}
            count={counts.retracted}
            label="retracted"
          />
          <StatusValue
            color="red100"
            fill={counts.applicants > 0 ? `${(counts.rejected / counts.applicants) * 100}%` : '0%'}
            count={counts.rejected}
            label="rejected"
          />
        </Stack>
      </Stack>
    </StatsCard>
  );
}

function StatusValue({
  color,
  count,
  label,
  fill,
}: {
  color: Color;
  count: number;
  label: string;
  fill: string;
}) {
  const { t } = useStore();
  return (
    <Stack orientation="horizontal" space={2} justify="space-between" align="center">
      <Text ellipsis font="bold14" color="purple100" text={t('job.statistics.overview.' + label)} />
      <Stack orientation="horizontal" space={4} align="center">
        <Text font="bold14" color="purple80" text={count} />
        <Ratio $color={color} $fill={fill}>
          <span />
        </Ratio>
      </Stack>
    </Stack>
  );
}

function Source({ sources }: { sources: JobStatistics['sources'] }) {
  const { t } = useStore();

  return (
    <StatsCard>
      <Stack space={4}>
        <Text font="bold14" color="purple80" caps text={t('job.statistics.overview.source')} />
        <Stack orientation="horizontal" space={4}>
          <Stack space={2} style={{ flexGrow: 1 }}>
            <SourceValue label="direct" count={sources.urls.direct?.count ?? 0} color="green100" />
            <SourceValue label="sourced" count={sources.sourced} color="blue100" />
            <SourceValue label="manual" count={sources.manuallyAdded} color="purple200" />
          </Stack>
          <SourcedDoughnut sources={sources} />
        </Stack>
      </Stack>
    </StatsCard>
  );
}

function SourceValue({ label, count, color }: { label: string; count: number; color: Color }) {
  const { t } = useStore();
  return (
    <Stack space={2} orientation="horizontal" justify="space-between" align="center">
      <Stack space={2} orientation="horizontal" align="center">
        <Circle $color={color} />
        <Text
          ellipsis
          font="bold14"
          color="purple100"
          text={t('job.statistics.overview.' + label)}
        />
      </Stack>
      <Text font="bold14" color="purple80" text={count} />
    </Stack>
  );
}

const Circle = styled.div<{ $color: Color }>((p) => ({
  backgroundColor: theme.color[p.$color],
  height: '0.75rem',
  width: '0.75rem',
  borderRadius: '100%',
  flexShrink: 0,
}));

const StatsCard = styled(Card).attrs({
  space: 10,
})({
  minHeight: '13.75rem',
  minWidth: 0,
  '@media(max-width: 1400px)': {
    padding: '1.5rem',
    minHeight: 0,
  },
});

const Ratio = styled.div<{
  $color: Color;
  $fill: string;
}>((p) => ({
  borderRadius: '9999px',
  backgroundColor: theme.color.purpleTrans10,
  width: '10rem',
  height: '0.75rem',
  '> span': {
    display: 'block',
    height: '100%',
    borderRadius: '9999px',
    width: p.$fill,
    backgroundColor: theme.color[p.$color],
  },
}));

const Container = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: '1.5rem',
  '@media(max-width: 1200px)': {
    display: 'flex',
    flexDirection: 'column',
  },
});
